/**
 * Alukap-SS Veranda Canopy Calculator — Rewritten
 * Reads from window.roofCalcData (pre-filled by Liquid section)
 * No external API calls, no tag-based filtering
 *
 * Key differences from Alukap-XR calculator:
 * — 6 roof types (all lean-to: Solid Sides × 3 + Open Sides × 3), no apex/single-slope
 * — Adds 35mm sheet thickness alongside 10mm/16mm/25mm
 * — Slope-B 3000mm gate splits products into low-profile and high-span pairs
 *   handled via the `appliesWhen` rule field (slope_b_lte_3000 / slope_b_gt_3000)
 * — PRODUCT 2 uses new qty_formula `ceil_a_div_2000_plus_1`
 * — PRODUCT 4 (Endcap LH+RH) uses new variant_driven_by `sku_fixed_pair_colour_suffix`
 * — size_a_split_length mode multiplies emitted quantities by override.multiplier
 *
 * Shared with XR:
 * — Same Step 2 state dimensions (sheet colour, thickness, bar colour)
 * — Same sku_map + colour suffix pattern for locale-proof SKU matching
 * — Same kit list UI at Step 4
 *
 * Fixes applied (sync with XR v7 and Snapa fixed):
 * — Sheet width fix: findSheetVariant enforces 690mm match for sheet products,
 *   falls back to plain label match for bars (no false skips)
 * — RAL Colour SKU lookup: key is 'ral colour' to match HTML option value
 * — Kit list: prefers variant.featuredImage over product.images[0]
 * — Removed hardcoded dev store hostname fallback in buildDiagramUrl
 */

(function () {
  'use strict';

  var data = window.roofCalcData;
  if (!data) {
    console.warn('Roof calculator: no data found. Check Liquid section.');
    return;
  }

  // ─── State ───────────────────────────────────────────────────────────────
  var state = {
    tag1: '',
    rfnm: 1,
    roofKey: '',
    roofName: '',
    colour: 'Clear',        // Sheet colour — Clear / Opal / Bronze
    thickness: '10mm',      // Sheet thickness — 10mm / 16mm / 25mm / 35mm
    barColour: 'White',     // Bar colour — White / Brown / RAL Colour
    sizeA: 0,
    sizeB: 0,
    sheets: 0,
    cartItems: []
  };

  // Bar colour → SKU suffix map. HTML option value is 'RAL Colour' (not 'Bespoke RAL Colour')
  var barColourSku = {
    'white': 'W',
    'brown': 'B',
    'ral colour': 'P'
  };

  // Bar colour → image filename token (e.g. alukap-ss-{roof}-{sheet}-{bar}.webp)
  var barColourImageToken = {
    'white': 'white',
    'brown': 'brown',
    'ral colour': 'ral'
  };


  // ─── Helpers ─────────────────────────────────────────────────────────────

  function findLengthTier(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        return sorted[i].variantLabel;
      }
    }
    return null;
  }

  function findVariantByLabel(product, label) {
    if (!product || !product.variants || !label) return null;
    var normLabel = label.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (normTitle.indexOf(normLabel) !== -1) {
        return product.variants[i];
      }
    }
    return null;
  }

  /**
   * Find Axiome sheet variant by matching BOTH calculator's sheet width
   * AND the length tier. Required for sheet products with width × length titles
   * (e.g. "690mm / 3000mm") — a plain substring match on "3000mm" would also
   * match "1050mm / 3000mm" variants.
   *
   * For products without a width dimension (e.g. bars with titles like
   * "2.0m / White"), the width match fails — we fall back to plain
   * findVariantByLabel behaviour so non-sheet products still work.
   */
  function findSheetVariant(product, tierLabel) {
    if (!product || !product.variants || !tierLabel) return null;
    var widthLabel = data.sheetWidthMm + 'mm';
    var normWidth = widthLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    var normTier = tierLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();

    // Check whether any variant on this product contains the sheet width
    var hasWidthDimension = false;
    for (var k = 0; k < product.variants.length; k++) {
      var checkTitle = product.variants[k].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (checkTitle.indexOf(normWidth) !== -1) {
        hasWidthDimension = true;
        break;
      }
    }

    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (hasWidthDimension) {
        if (normTitle.indexOf(normWidth) !== -1 && normTitle.indexOf(normTier) !== -1) {
          return product.variants[i];
        }
      } else {
        if (normTitle.indexOf(normTier) !== -1) {
          return product.variants[i];
        }
      }
    }
    return null;
  }

  function findVariantBySku(product, sku) {
    if (!product || !product.variants || !sku) return null;
    var normSku = sku.toUpperCase();
    for (var i = 0; i < product.variants.length; i++) {
      if ((product.variants[i].sku || '').toUpperCase() === normSku) {
        return product.variants[i];
      }
    }
    return null;
  }

  function splitLength(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    var results = [];
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        results.push({ variantLabel: sorted[i].variantLabel, quantity: 1 });
        return results;
      }
    }
    var longest = sorted[sorted.length - 1];
    var fullLengths = Math.floor(measurementMm / longest.maxLength);
    var remainder = measurementMm % longest.maxLength;
    if (fullLengths > 0) {
      results.push({ variantLabel: longest.variantLabel, quantity: fullLengths });
    }
    if (remainder > 0) {
      for (var j = 0; j < sorted.length; j++) {
        if (remainder <= sorted[j].maxLength) {
          results.push({ variantLabel: sorted[j].variantLabel, quantity: 1 });
          break;
        }
      }
    }
    return results;
  }

  function getOverride(rule) {
    var overrides = rule.roofTypeOverrides;
    if (typeof overrides === 'string') {
      try { overrides = JSON.parse(overrides); } catch (e) { return null; }
    }
    if (!overrides) return null;
    return overrides[state.roofKey] || null;
  }

  function getSkuMap(rule) {
    var map = rule.skuMap;
    if (!map) return null;
    if (typeof map === 'string') {
      try { map = JSON.parse(map); } catch (e) { return null; }
    }
    return map;
  }

  function gatePasses(rule) {
    var gate = rule.appliesWhen;
    if (!gate || gate === 'always') return true;
    if (gate === 'slope_b_lte_3000') return state.sizeB <= 3000;
    if (gate === 'slope_b_gt_3000') return state.sizeB > 3000;
    return true;
  }

  function getProduct(rule) {
    var products = rule.products;
    if (!products || products.length === 0) return null;
    if (products.length === 1) return products[0];

    var colourCode = { clear: 'C', opal: 'O', bronze: 'B' };
    var code = colourCode[state.colour.toLowerCase()] || '';
    var thicknessNum = parseInt(state.thickness) || 0;
    var skuPrefix = 'AS' + thicknessNum + code;

    for (var i = 0; i < products.length; i++) {
      if (products[i].variants && products[i].variants.length > 0) {
        var sku = products[i].variants[0].sku || '';
        if (sku.indexOf(skuPrefix) === 0) {
          return products[i];
        }
      }
    }
    return products[0];
  }

  function getBarColourSuffix() {
    return barColourSku[state.barColour.toLowerCase()] || 'W';
  }

  function getBarColourImageToken() {
    return barColourImageToken[state.barColour.toLowerCase()] || 'white';
  }


  // ─── Core calculation ────────────────────────────────────────────────────

  function calculateKit() {
    state.cartItems = [];
    var rules = data.productRules;

    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];

      if (!gatePasses(rule)) continue;

      var override = getOverride(rule);
      var product = getProduct(rule);
      if (!override) continue;
      if (!product) continue;

      var formula = rule.qtyFormula;
      var variantDrivenBy = rule.variantDrivenBy;
      var quantity = 0;
      var variant = null;
      var skuMap = getSkuMap(rule);
      var colourSuffix = getBarColourSuffix();

      if (variantDrivenBy === 'size_b_length_tier') {
        var tierLabel = findLengthTier(rule.lengthTiers, state.sizeB);
        if (!tierLabel) {
          showMaxLengthMessage();
          return;
        }
        // Use findSheetVariant: enforces sheet-width match for sheet products,
        // falls back to plain label match for bars
        variant = findSheetVariant(product, tierLabel);

      } else if (variantDrivenBy === 'size_a_split_length') {
        var splits = splitLength(rule.lengthTiers, state.sizeA);
        var splitMultiplier = override.multiplier || 1;
        for (var s = 0; s < splits.length; s++) {
          var splitVariant = findVariantByLabel(product, splits[s].variantLabel);
          if (splitVariant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: splitVariant,
              quantity: splits[s].quantity * splitMultiplier,
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'thickness_match') {
        variant = findVariantByLabel(product, state.thickness);

      } else if (variantDrivenBy === 'colour_thickness_size_a_split') {
        var p7splits = splitLength(rule.lengthTiers, state.sizeA);
        var p7multiplier = override.multiplier || 1;
        for (var p7s = 0; p7s < p7splits.length; p7s++) {
          var matchStr = state.barColour + ' / ' + state.thickness + ' / ' + p7splits[p7s].variantLabel;
          var p7variant = findVariantByLabel(product, matchStr);
          if (!p7variant) {
            p7variant = findVariantByLabel(product, p7splits[p7s].variantLabel);
          }
          if (p7variant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: p7variant,
              quantity: p7splits[p7s].quantity * p7multiplier,
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'colour_size_b_length_tier') {
        // Bar colour + Size B length tier (no thickness, no SKU map)
        // Used by: Low Profile / High Span — Wall Bar, Gable Bar, Glazing Bar
        // Variant title format: "[barColour] / [length]" (e.g. "White / 3.0m")
        // Locale-proof — findVariantByLabel normalises commas/spaces
        var colourBTier = findLengthTier(rule.lengthTiers, state.sizeB);
        if (!colourBTier) {
          showMaxLengthMessage();
          return;
        }
        var colourBMatch = state.barColour + ' / ' + colourBTier;
        variant = findVariantByLabel(product, colourBMatch);
        // Fallback: length-only match if combined match fails
        if (!variant) {
          variant = findVariantByLabel(product, colourBTier);
        }

      } else if (variantDrivenBy === 'colour_size_a_split') {
        // Bar colour + Size A split-length (no thickness, no SKU map)
        // Used by: Low Profile / High Span Cap, Top Wall Flashing
        // Variant title format: "[barColour] / [length]" (e.g. "Brown / 4.8m")
        // splitLength may emit multiple cart items per rule
        // Naming matches XR JS for cross-calculator consistency
        var colourAsplits = splitLength(rule.lengthTiers, state.sizeA);
        var colourAmultiplier = override.multiplier || 1;
        for (var ca = 0; ca < colourAsplits.length; ca++) {
          var caMatch = state.barColour + ' / ' + colourAsplits[ca].variantLabel;
          var caVariant = findVariantByLabel(product, caMatch);
          // Fallback: length-only match if combined match fails
          if (!caVariant) {
            caVariant = findVariantByLabel(product, colourAsplits[ca].variantLabel);
          }
          if (caVariant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: caVariant,
              quantity: colourAsplits[ca].quantity * colourAmultiplier,
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'sku_fixed_pair_colour_suffix') {
        var qtyForPair = override.fixed_qty || 0;
        if (qtyForPair > 0 && skuMap) {
          for (var pairKey in skuMap) {
            if (!skuMap.hasOwnProperty(pairKey)) continue;
            var fullSku = skuMap[pairKey] + colourSuffix;
            var pairVariant = findVariantBySku(product, fullSku);
            if (pairVariant) {
              state.cartItems.push({
                rule: rule,
                product: product,
                variant: pairVariant,
                quantity: qtyForPair,
                editable: true
              });
            }
          }
        }
        continue;

      } else if (variantDrivenBy === 'sku_colour_suffix') {
        // Colour-only products (e.g. PRODUCT 2 Wall & Eaves Beam Post)
        // Match variant whose SKU ends with bar colour suffix (W/B/P) — locale-proof
        for (var v = 0; v < product.variants.length; v++) {
          var vSku = (product.variants[v].sku || '').toUpperCase();
          if (vSku.slice(-1) === colourSuffix) {
            variant = product.variants[v];
            break;
          }
        }
        // Fallback: match colour in variant title (RAL titled "RAL Colour")
        if (!variant) {
          var colourTitle = state.barColour.toLowerCase() === 'bespoke ral colour' ? 'RAL' : state.barColour;
          variant = findVariantByLabel(product, colourTitle);
        }
        // Falls through to qty_formula (e.g. ceil_a_div_2000_plus_1)

      } else if (variantDrivenBy === 'fixed') {
        if (rule.fixedVariantLabel) {
          variant = findVariantByLabel(product, rule.fixedVariantLabel);
        } else {
          variant = product.variants[0];
        }
      }

      if (!variant) continue;

      if (formula === 'ceil_a_div_700') {
        var divisor = data.sheetWidthMm + data.sheetGapMm;
        quantity = Math.ceil(state.sizeA / divisor);
        var multiplier = override.multiplier || 1;
        quantity = quantity * multiplier;
        state.sheets = Math.ceil(state.sizeA / divisor);

      } else if (formula === 'sheets_minus') {
        var sheetCount = state.sheets;
        var apexMultiplier = override.multiplier || 1;
        quantity = (sheetCount * apexMultiplier) - (override.sheets_minus || 0);
        if (quantity < 0) quantity = 0;

      } else if (formula === 'fixed') {
        quantity = override.fixed_qty || 0;

      } else if (formula === 'ceil_a_div_2100') {
        quantity = Math.ceil(state.sizeA / 2100);
        quantity = quantity * (override.multiplier || 1);

      } else if (formula === 'ceil_a_div_2000_plus_1') {
        quantity = Math.ceil(state.sizeA / 2000) + 1;
        quantity = quantity * (override.multiplier || 1);

      } else if (formula === 'ceil_measurement_div_10000') {
        var measurement = 0;
        if (override.measurement === 'a') {
          measurement = state.sizeA;
        } else if (override.measurement === 'a_plus_b') {
          measurement = state.sizeA + state.sizeB;
        } else if (override.measurement === 'b_times_2') {
          measurement = state.sizeB * 2;
        }
        quantity = Math.ceil(measurement / 10000);
        if (quantity < 1) quantity = 1;
      }

      if (quantity > 0) {
        state.cartItems.push({
          rule: rule,
          product: product,
          variant: variant,
          quantity: quantity,
          editable: true
        });
      }
    }

    renderKitList();
  }


  // ─── Editable kit list (Step 4) ──────────────────────────────────────────

  function renderKitList() {
    var container = document.querySelector('.dkit .kit-list');
    var mcontainer = document.querySelector('.mkit .kit-list');
    if (!container) {
      console.warn('Roof calculator: .kit-list container not found');
      return;
    }

    var html = '';
    var mhtml = '';
    var total = 0;
    var totalSaved = 0;

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      var price = item.variant.price / 100;
      var compareAt = item.variant.compareAtPrice ? item.variant.compareAtPrice / 100 : null;
      var lineTotal = price * item.quantity;
      var lineSaving = compareAt ? (compareAt - price) * item.quantity : 0;
      total += lineTotal;
      totalSaved += lineSaving;

      var image = '';
      // Prefer variant-specific image (correct colour for products with image-per-variant)
      // Fall back to first product image if variant has none
      if (item.variant && item.variant.featuredImage) {
        image = item.variant.featuredImage;
      } else if (item.product && item.product.images && item.product.images.length > 0) {
        image = item.product.images[0];
      }

      html += '<div class="kit-item" data-index="' + i + '">';
      html += '  <div class="kit-item-image">';
      if (image) {
        html += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      }
      html += '  </div>';
      html += '  <div class="kit-item-details">';
      html += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      html += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      html += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        html += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        html += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        html += '    <span>' + formatMoney(price) + '</span>';
      }
      html += '  </div>';
      html += '  </div>';
      html += '  <div class="kit-item-qty">';
      html += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      html += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      html += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      html += '  </div>';
      html += '  <div class="kit-item-price">' + formatMoney(lineTotal) + '</div>';
      html += '</div>';

      mhtml += '<div class="kit-item" data-index="' + i + '">';
      mhtml += '  <div class="w-100"><div class="w-25">';
      mhtml += '  <div class="kit-item-image">';
      if (image) {
        mhtml += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      }
      mhtml += '  </div>';
      mhtml += '  </div><div class="w-75">';
      mhtml += '  <div class="kit-item-details">';
      mhtml += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      mhtml += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      mhtml += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        mhtml += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        mhtml += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        mhtml += '    <span>' + formatMoney(price) + '</span>';
      }
      mhtml += '  </div>';
      mhtml += '  </div>';
      mhtml += '  <div class="kit-item-qty">';
      mhtml += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      mhtml += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      mhtml += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      mhtml += '  </div>';
      mhtml += '  </div></div>';
      mhtml += '</div>';
    }

    container.innerHTML = html;
    if (mcontainer) mcontainer.innerHTML = mhtml;

    $('.kitcart .total span').html(formatMoney(total));
    if (totalSaved > 0) {
      var ysav = 'You saved ';
      if (window.location.pathname.indexOf('fr-fr') === 1) { ysav = 'Vous avez enregistré '; }
      $('.kitcart .saved').show().html(ysav + formatMoney(totalSaved) + '!');
    } else {
      $('.kitcart .saved').hide();
    }

    showStep('kit');
    bindKitListEvents();
  }

  function bindKitListEvents() {
    document.querySelectorAll('.kit-qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        if (state.cartItems[idx].quantity > 0) {
          state.cartItems[idx].quantity--;
          renderKitList();
        }
      });
    });

    document.querySelectorAll('.kit-qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        state.cartItems[idx].quantity++;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-qty-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        var val = parseInt(this.value) || 0;
        if (val < 0) val = 0;
        state.cartItems[idx].quantity = val;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.kit-add-to-cart').forEach(function (b) {
          b.setAttribute('disabled', 'disabled');
          if (window.location.pathname.indexOf('fr-fr') === 1) {
            b.textContent = 'Ajout au panier...';
          } else {
            b.textContent = 'Adding to cart...';
          }
        });
        addToCart();
      });
    });
  }

  function formatMoney(amount) {
    var currency = data.currency || {};
    var locale = currency.locale || 'en-GB';
    var code = currency.code || 'GBP';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code
      }).format(amount);
    } catch (e) {
      return (currency.symbol || '£') + amount.toFixed(2);
    }
  }


  // ─── Cart submission ──────────────────────────────────────────────────────

  function addToCart() {
    var items = [];

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (item.quantity < 1) continue;
      items.push({
        quantity: item.quantity,
        id: item.variant.id,
        properties: {
          '_ROOF TYPE': state.roofName,
          '_COLOUR': state.colour,
          '_LENGTH A': state.sizeA,
          '_LENGTH B': state.sizeB
        }
      });
    }

    if (items.length === 0) {
      alert('No items to add. Please check your quantities.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
      return;
    }

    items.reverse();

    jQuery.post('/cart/add.js', {
      items: items
    }).then(function () {
      if (window.location.pathname.indexOf('fr-fr') === 1) {
        window.location.href = data.cartUrl || '/fr-fr/cart';
      } else {
        window.location.href = data.cartUrl || '/cart';
      }
    }).fail(function () {
      alert('There was an error adding to cart. Please try again.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
    });
  }


  function showMaxLengthMessage() {
    var msg = data.maxLengthMessage || 'This size is too long, please get free advice on Live Chat';
    alert(msg);
  }


  // ─── UI step management ───────────────────────────────────────────────────

  function showStep(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();
    if (step === 'rooftype') {
      $('.rooftype').show();
      updateStepIndicators('wtor');
    } else if (step === 'roofsheet') {
      $('.roofsheet').show();
      updateStepIndicators('yrc');
    } else if (step === 'roofsize') {
      $('.roofsize').show();
      updateStepIndicators('cyrs');
    } else if (step === 'kit') {
      $('.kit-list-wrapper').show();
      updateStepIndicators('kit');
    }
  }

  function updateStepIndicators(currentStep) {
    $(".roofcal .number").removeClass("current").removeClass("complete");
    var steps = ['wtor', 'yrc', 'cyrs', 'kit'];
    var currentIdx = steps.indexOf(currentStep);
    for (var i = 0; i < steps.length; i++) {
      if (i < currentIdx) {
        $(".roofcal .number." + steps[i]).addClass("complete");
      } else if (i === currentIdx) {
        $(".roofcal .number." + steps[i]).addClass("current");
      }
    }
  }


  // ─── Step 2+ dynamic image swap ───────────────────────────────────────────

  function buildDiagramUrl() {
    if (!state.roofKey) return '';
    var roofKebab = state.roofKey.replace(/_/g, '-');
    var sheetToken = state.colour.toLowerCase();
    var barToken = getBarColourImageToken();
    // Base path provided by Liquid section. Hardcoded fallback to production CDN
    // if Liquid data is missing (no dev-store hostname switching).
    var basePath = data.imagesBasePath || '//clearambershorush.com/cdn/shop/files/';
    return basePath + 'alukap-ss-' + roofKebab + '-' + sheetToken + '-' + barToken + '.webp';
  }

  function updateDiagramImage() {
    var url = buildDiagramUrl();
    if (!url) return;
    $('.roofsheet img, .roofsize img, .qimg img').attr('src', url);
  }


  // ─── Event bindings ──────────────────────────────────────────────────────
  // Wrapped in bindEvents() so we can wait for jQuery + DOM ready before binding.

  function bindEvents() {

    $(".roofcal .roofimg").click(function () {
      var roofIndex = parseInt($(this).attr('key')) - 1;
      var roofType = data.roofTypes[roofIndex];
      if (!roofType) return;

      state.roofKey = roofType.key;
      state.roofName = roofType.name;
      state.tag1 = $(this).attr('title');
      state.rfnm = $(this).attr('key');

      updateDiagramImage();

      $('select[name="colour1"]').val('Clear').trigger('change');
      $('select[name="thickness"]').val('10mm').trigger('change');
      $('select[name="barcolour"]').val('White').trigger('change');

      showStep('roofsheet');
    });

    $('.srtype').click(function () {
      state.roofKey = '';
      state.roofName = '';
      showStep('rooftype');
    });

    $('select[name="colour1"]').change(function () {
      state.colour = $(this).val();
      var text = this.options[this.selectedIndex].text;
      $('.quote .clr').text(text);
      updateDiagramImage();
    });

    $('select[name="thickness"]').change(function () {
      state.thickness = $(this).val();
      $('.quote .thick').text(state.thickness);
    });

    $('select[name="barcolour"]').change(function () {
      state.barColour = $(this).val();
      var text = this.options[this.selectedIndex].text;
      $('.quote .barclr').text(text);
      updateDiagramImage();
    });

    $('.rfsize').click(function () {
      if (!state.roofKey) {
        alert('Please select a Roof Type');
        return false;
      }
      if (!state.colour) {
        alert('Please select a Sheet Colour');
        return false;
      }
      if (!state.thickness) {
        alert('Please select a Sheet Thickness');
        return false;
      }
      if (!state.barColour) {
        alert('Please select a Bar Colour');
        return false;
      }
      showStep('roofsize');
    });

    $(".roofcal .yrc").click(function () {
      if (!state.roofKey) return false;
      showStep('roofsheet');
    });

    $('.ckit').click(function () {
      $(this).attr('disabled', 'disabled');

      state.sizeA = parseInt($('.roofinput input[name="lengtha"]').val()) || 0;
      state.sizeB = parseInt($('.roofinput input[name="lengthb"]').val()) || 0;

      if (state.sizeA < 99 || state.sizeB < 99) {
        alert(data.minSizeMessage || 'Please enter valid length and width');
        $(this).attr('disabled', false);
        return false;
      }

      $('.quote .sizea span').text(state.sizeA);
      $('.quote .sizeb span').text(state.sizeB);

      if (state.sizeB > 5500) {
        showMaxLengthMessage();
        $(this).attr('disabled', false);
        return false;
      }

      calculateKit();
      $(this).attr('disabled', false);
    });

    $(".roofinput input").change(function () {
      $(".ckit").attr("disabled", false);
    });

  }


  // ─── jQuery-ready boot ───────────────────────────────────────────────────
  // Wait for jQuery to be defined AND the DOM to be ready before binding events.
  // Handles the case where this script loads before jQuery (e.g. defer ordering).

  function bootWhenReady() {
    if (typeof jQuery === 'undefined') {
      var waitForJQuery = setInterval(function () {
        if (typeof jQuery !== 'undefined') {
          clearInterval(waitForJQuery);
          jQuery(document).ready(bindEvents);
        }
      }, 50);
      setTimeout(function () {
        if (typeof jQuery === 'undefined') {
          clearInterval(waitForJQuery);
          console.error('Roof calculator: jQuery did not load within 10 seconds. Calculator cannot initialise.');
        }
      }, 10000);
    } else {
      jQuery(document).ready(bindEvents);
    }
  }

  bootWhenReady();

})();